/**
 * ChatProvider - Global chat provider component
 *
 * Wrap your app with this provider to enable the global chat system.
 * Renders the ChatDrawerV2 at the app root level.
 *
 * @example
 * ```tsx
 * // In your App.tsx
 * function App() {
 *   return (
 *     <MantineProvider>
 *       <ChatProvider>
 *         <Routes />
 *       </ChatProvider>
 *     </MantineProvider>
 *   );
 * }
 * ```
 */

import { ReactNode, useCallback } from 'react';
import { ChatDrawerV2 } from '@components/Chat/v2';
import { useChatStore } from '@stores/chatStore';

interface ChatProviderProps {
  children: ReactNode;
  /** Optional callback when VIEW PROFILE is clicked in chat header */
  onViewProfile?: (entityId: number, entityType: 'worker' | 'company') => void;
}

function ChatProvider({ children, onViewProfile }: ChatProviderProps) {
  const activeChat = useChatStore((state) => state.activeChat);

  // Handle view profile click
  const handleViewProfile = useCallback(() => {
    if (activeChat && onViewProfile) {
      onViewProfile(activeChat.entity.id, activeChat.entity.type);
    }
  }, [activeChat, onViewProfile]);

  return (
    <>
      {children}
      <ChatDrawerV2
        onViewProfile={onViewProfile ? handleViewProfile : undefined}
      />
    </>
  );
}

export default ChatProvider;
